import { createReducer } from "reduxsauce";
import pick from "lodash/pick";
import { EventsTypes as Types, UserTypes } from "../actions";
import storeInitialState from "../store/initial-state";

export const INITIAL_STATE = storeInitialState.events;

export const clearEvents = (state, action) => ({
  ...state,
  data: [],
});

export const createEventFailure = (state, action) => ({
  ...state,
  adding: false,
});
export const createEventRequest = (state, action) => ({
  ...state,
  adding: true,
});
export const createEventSuccess = (state, action) => ({
  ...state,
  adding: false,
});

export const createManualEventFailure = (state, action) => ({
  ...state,
  adding: false,
});
export const createManualEventRequest = (state, action) => ({
  ...state,
  adding: true,
});
export const createManualEventSuccess = (state, action) => ({
  ...state,
  adding: false,
});

export const updateManualEventFailure = (state, action) => ({
  ...state,
  updating: false,
});
export const updateManualEventRequest = (state, action) => ({
  ...state,
  updating: true,
});
// The edited visit replaces both the row in the list and the selected detail,
// so the sidebar and the table agree without another fetch.
export const updateManualEventSuccess = (state, action) => {
  const { eventData } = action;

  return {
    ...state,
    updating: false,
    selected: eventData,
    data: state.data.map(event =>
      event.id === eventData.id ? { ...event, ...eventData } : event,
    ),
  };
};

export const getEventMembersFailure = (state, action) => ({
  ...state,
  loadingMembers: false,
});
export const getEventMembersRequest = (state, action) => ({
  ...state,
  loadingMembers: true,
});
export const getEventMembersSuccess = (state, action) => ({
  ...state,
  loadingMembers: false,
  members: action.members,
});

export const createEventMemberFailure = (state, action) => ({
  ...state,
  addingMember: false,
});
export const createEventMemberRequest = (state, action) => ({
  ...state,
  addingMember: true,
});
// The list is re-fetched after a successful add, so the new row only needs to
// be appended here for the case where a caller reads state before that lands.
export const createEventMemberSuccess = (state, action) => ({
  ...state,
  addingMember: false,
  members: [...state.members, action.member],
});

/**
 * Attendance for one student on the visit currently open in the detail panel
 * (`selected`). The checkbox flips on REQUEST so it responds to the click, and
 * FAILURE puts the original value back — the action carries it rather than the
 * reducer remembering, so a failed call cannot be undone against a row that
 * has since changed.
 */
const patchRegistration = (state, registrationId, patch) => {
  const registrations = state.selected?.student_registrations;

  if (!registrations) return state;

  return {
    ...state,
    selected: {
      ...state.selected,
      student_registrations: registrations.map(registration =>
        registration.id === registrationId
          ? { ...registration, ...patch }
          : registration,
      ),
    },
  };
};

export const markAttendanceRequest = (state, action) =>
  patchRegistration(state, action.registrationId, {
    attended: action.attended,
  });

export const markAttendanceFailure = (state, action) =>
  patchRegistration(state, action.registrationId, {
    attended: action.attended,
  });

// The server is the authority on attended_at, so the whole row is replaced.
export const markAttendanceSuccess = (state, action) =>
  patchRegistration(
    state,
    action.registration.id,
    action.registration,
  );

// The counselor added a student, so the row is appended to the register the
// panel is already showing rather than refetching the whole visit. Guarded
// against a duplicate id so a double submit cannot list the same student twice.
export const addRegistrationSuccess = (state, action) => {
  const registrations = state.selected?.student_registrations;

  if (!registrations) return state;
  if (registrations.some(r => r.id === action.registration.id))
    return state;

  return {
    ...state,
    selected: {
      ...state.selected,
      student_registrations: [...registrations, action.registration],
    },
  };
};

export const deleteEventMemberFailure = (state, action) => ({
  ...state,
  removingMember: false,
});
export const deleteEventMemberRequest = (state, action) => ({
  ...state,
  removingMember: true,
});
export const deleteEventMemberSuccess = (state, action) => ({
  ...state,
  removingMember: false,
  members: state.members.filter(member => member.id !== action.id),
});

export const downloadEventsFailure = (state, action) => ({
  ...state,
  downloadUrl: null,
  downloading: false,
});
export const downloadEventsRequest = (state, action) => ({
  ...state,
  downloadUrl: null,
  downloading: true,
});
export const downloadEventsSuccess = (state, action) => ({
  ...state,
  downloadUrl: action.url,
  downloading: false,
});

export const getEventsFailure = (state, action) => ({
  ...state,
  loading: false,
});
export const getEventsRequest = (state, action) => ({
  ...state,
  loading: true,
});
export const getEventsSuccess = (state, action) => {
  const {
    eventsData: { events, matched, total, total_without_group },
    params,
  } = action;

  return {
    ...state,
    data: params.page_no === 1 ? events : [...state.data, ...events],
    matched,
    total_without_group,
    total,
    params: { ...state.params, ...params },
    loading: false,
  };
};

export const getEventFailure = (state, action) => ({
  ...state,
  loadingSelected: false,
});
export const getEventRequest = (state, action) => ({
  ...state,
  loadingSelected: true,
  selected: null,
});
export const getEventSuccess = (state, action) => ({
  ...state,
  loadingSelected: false,
  selected: action.eventData,
});

export const rescheduleEventFailure = (state, action) => ({
  ...state,
  rescheduling: false,
});
export const rescheduleEventRequest = (state, action) => ({
  ...state,
  rescheduling: true,
});
export const rescheduleEventSuccess = (state, action) => {
  const { eventData } = action;

  return {
    ...state,
    data: state.data.map(event =>
      event.id === eventData.id
        ? {
            ...event,
            ...pick(eventData, [
              "status",
              "event_date",
              "time_from",
              "time_to",
              "timezone",
            ]),
          }
        : event,
    ),
    selected: eventData,
    rescheduling: false,
  };
};

export const confirmEventFailure = (state, action) => ({
  ...state,
  confirming: false,
});
export const confirmEventRequest = (state, action) => ({
  ...state,
  confirming: true,
});
export const confirmEventSuccess = (state, action) => {
  const { eventData } = action;

  return {
    ...state,
    data: state.data.map(event =>
      event.id === eventData.id
        ? {
            ...event,
            ...pick(eventData, [
              "status",
              "event_date",
              "time_from",
              "time_to",
            ]),
          }
        : event,
    ),
    selected: eventData,
    confirming: false,
  };
};

export const cancelEventFailure = (state, action) => ({
  ...state,
  cancelling: false,
});
export const cancelEventRequest = (state, action) => ({
  ...state,
  cancelling: true,
});
export const cancelEventSuccess = (state, action) => {
  const { eventData } = action;

  return {
    ...state,
    data: state.data.map(event =>
      event.id === eventData.id
        ? {
            ...event,
            ...pick(eventData, [
              "status",
              "event_date",
              "time_from",
              "time_to",
            ]),
          }
        : event,
    ),
    selected: eventData,
    cancelling: false,
  };
};

export const updateLocationFailure = (state, action) => ({
  ...state,
  updating: false,
});
export const updateLocationRequest = (state, action) => ({
  ...state,
  updating: true,
});
export const updateLocationSuccess = (state, action) => {
  const { eventData } = action;

  return {
    ...state,
    data: state.data.map(event =>
      event.id === eventData.id
        ? {
            ...event,
            ...pick(eventData, [
              "status",
              "event_date",
              "time_from",
              "time_to",
            ]),
          }
        : event,
    ),
    selected: eventData,
    updating: false,
  };
};

const logout = (state, action) => INITIAL_STATE;

// map our action types to our reducer functions
export const HANDLERS = {
  [Types.CLEAR_EVENTS]: clearEvents,
  [Types.CREATE_EVENT_FAILURE]: createEventFailure,
  [Types.CREATE_EVENT_REQUEST]: createEventRequest,
  [Types.CREATE_EVENT_SUCCESS]: createEventSuccess,
  [Types.CREATE_MANUAL_EVENT_FAILURE]: createManualEventFailure,
  [Types.CREATE_MANUAL_EVENT_REQUEST]: createManualEventRequest,
  [Types.CREATE_MANUAL_EVENT_SUCCESS]: createManualEventSuccess,
  [Types.UPDATE_MANUAL_EVENT_FAILURE]: updateManualEventFailure,
  [Types.UPDATE_MANUAL_EVENT_REQUEST]: updateManualEventRequest,
  [Types.UPDATE_MANUAL_EVENT_SUCCESS]: updateManualEventSuccess,
  [Types.GET_EVENT_MEMBERS_FAILURE]: getEventMembersFailure,
  [Types.GET_EVENT_MEMBERS_REQUEST]: getEventMembersRequest,
  [Types.GET_EVENT_MEMBERS_SUCCESS]: getEventMembersSuccess,
  [Types.CREATE_EVENT_MEMBER_FAILURE]: createEventMemberFailure,
  [Types.CREATE_EVENT_MEMBER_REQUEST]: createEventMemberRequest,
  [Types.CREATE_EVENT_MEMBER_SUCCESS]: createEventMemberSuccess,
  [Types.MARK_ATTENDANCE_FAILURE]: markAttendanceFailure,
  [Types.MARK_ATTENDANCE_REQUEST]: markAttendanceRequest,
  [Types.MARK_ATTENDANCE_SUCCESS]: markAttendanceSuccess,
  [Types.ADD_REGISTRATION_SUCCESS]: addRegistrationSuccess,
  [Types.DELETE_EVENT_MEMBER_FAILURE]: deleteEventMemberFailure,
  [Types.DELETE_EVENT_MEMBER_REQUEST]: deleteEventMemberRequest,
  [Types.DELETE_EVENT_MEMBER_SUCCESS]: deleteEventMemberSuccess,
  [Types.DOWNLOAD_EVENTS_FAILURE]: downloadEventsFailure,
  [Types.DOWNLOAD_EVENTS_REQUEST]: downloadEventsRequest,
  [Types.DOWNLOAD_EVENTS_SUCCESS]: downloadEventsSuccess,
  [Types.GET_EVENTS_FAILURE]: getEventsFailure,
  [Types.GET_EVENTS_REQUEST]: getEventsRequest,
  [Types.GET_EVENTS_SUCCESS]: getEventsSuccess,
  [Types.GET_EVENT_FAILURE]: getEventFailure,
  [Types.GET_EVENT_REQUEST]: getEventRequest,
  [Types.GET_EVENT_SUCCESS]: getEventSuccess,
  [Types.RESCHEDULE_EVENT_FAILURE]: rescheduleEventFailure,
  [Types.RESCHEDULE_EVENT_REQUEST]: rescheduleEventRequest,
  [Types.RESCHEDULE_EVENT_SUCCESS]: rescheduleEventSuccess,
  [Types.CONFIRM_EVENT_FAILURE]: confirmEventFailure,
  [Types.CONFIRM_EVENT_REQUEST]: confirmEventRequest,
  [Types.CONFIRM_EVENT_SUCCESS]: confirmEventSuccess,
  [Types.CANCEL_EVENT_FAILURE]: cancelEventFailure,
  [Types.CANCEL_EVENT_REQUEST]: cancelEventRequest,
  [Types.CANCEL_EVENT_SUCCESS]: cancelEventSuccess,
  [Types.UPDATE_LOCATION_FAILURE]: updateLocationFailure,
  [Types.UPDATE_LOCATION_REQUEST]: updateLocationRequest,
  [Types.UPDATE_LOCATION_SUCCESS]: updateLocationSuccess,
  [UserTypes.LOGOUT]: logout,
};

export default createReducer(INITIAL_STATE, HANDLERS);
